/**
 * Tapo Camera Finder — multi-step wizard + product matching engine
 */
(function () {
  if (window.customElements.get('camera-finder')) return;

  const BUDGET_RANGES = {
    'under-50': { min: 0, max: 5000 },
    '50-100': { min: 5000, max: 10000 },
    '100-150': { min: 10000, max: 15000 },
    '150-200': { min: 15000, max: 20000 },
    'over-200': { min: 20000, max: Infinity },
  };

  const POWER_ALIASES = {
    wired: ['wired'],
    battery: ['battery', 'battery_solar'],
    solar: ['solar', 'battery_solar'],
    battery_solar: ['battery_solar', 'solar', 'battery'],
  };

  const PLACEHOLDER_IMAGE =
    'data:image/svg+xml,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="#f3f4f6" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="sans-serif" font-size="14">No image</text></svg>'
    );

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const amount = Number(cents);
    if (Number.isNaN(amount)) return '';

    const format =
      moneyFormat ||
      window.themeVariables?.settings?.moneyFormat ||
      window.themeVariables?.settings?.moneyWithCurrencyFormat;

    if (typeof window.formatMoney === 'function' && format) {
      return window.formatMoney(amount, format);
    }

    return (amount / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'EUR',
    });
  }

  function productImage(product) {
    return product?.image || PLACEHOLDER_IMAGE;
  }

  function scoreProduct(product, answers) {
    let score = 0;

    if (product.usage.includes(answers.usage)) {
      score += 25;
    } else if (answers.usage === 'outdoor' && product.usage.includes('indoor')) {
      score += 8;
    }

    const acceptedPower = POWER_ALIASES[answers.power] || [answers.power];
    if (acceptedPower.includes(product.power)) {
      score += 20;
    } else if (answers.power === 'battery_solar' && product.power === 'solar') {
      score += 14;
    } else if (answers.power === 'solar' && product.power === 'battery_solar') {
      score += 16;
    }

    if (product.quality === answers.quality) {
      score += 20;
    } else if (answers.quality === '4k' && product.quality === '2k') {
      score += 6;
    } else if (answers.quality === '2k' && product.quality === '4k') {
      score += 12;
    }

    if (answers.rotation360 === 'yes') {
      score += product.has360 ? 15 : 0;
    } else {
      score += product.has360 ? 10 : 15;
    }

    if (answers.aiDetection === 'yes') {
      score += product.hasAi ? 10 : 0;
    } else {
      score += product.hasAi ? 7 : 10;
    }

    const range = BUDGET_RANGES[answers.budget];
    if (range && product.price >= range.min && product.price <= range.max) {
      score += 10;
    } else if (range && product.price < range.max + 2000 && product.price > range.min - 2000) {
      score += 5;
    }

    return score;
  }

  function matchProducts(products, answers) {
    const ranked = products
      .filter((product) => Array.isArray(product.usage) && product.usage.length > 0)
      .map((product) => ({ product, score: scoreProduct(product, answers) }))
      .sort((a, b) => b.score - a.score || a.product.price - b.product.price);

    return {
      recommended: ranked[0]?.product || null,
      alternatives: ranked.slice(1, 4).map((r) => r.product),
    };
  }

  class CameraFinder extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-camera-finder-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch {
        return;
      }

      this.products = this.config.products || [];
      this.labels = this.config.labels || {};
      this.steps = this.config.steps || [];
      this.moneyFormat = this.config.moneyFormat;
      this.currentStep = 0;
      this.answers = {};

      this.wizardEl = this.querySelector('[data-wizard]');
      this.resultsEl = this.querySelector('[data-results]');
      this.stepContainer = this.querySelector('[data-step-container]');
      this.progressEl = this.querySelector('[data-progress]');
      this.backBtn = this.querySelector('[data-back]');
      this.nextBtn = this.querySelector('[data-next]');

      this._onBack = () => this.goBack();
      this._onNext = () => this.goNext();

      this.backBtn?.addEventListener('click', this._onBack);
      this.nextBtn?.addEventListener('click', this._onNext);

      this.renderProgress();
      this.renderStep();
      this.updateNav();
    }

    disconnectedCallback() {
      this.backBtn?.removeEventListener('click', this._onBack);
      this.nextBtn?.removeEventListener('click', this._onNext);
    }

    renderProgress() {
      if (!this.progressEl) return;
      this.progressEl.innerHTML = this.steps
        .map((_, i) => {
          const active = i === this.currentStep ? ' is-active' : i < this.currentStep ? ' is-done' : '';
          return `<span class="cf-dot${active}" role="presentation"></span>`;
        })
        .join('');
    }

    renderStep() {
      const step = this.steps[this.currentStep];
      if (!step || !this.stepContainer) return;

      const selected = this.answers[step.id];

      if (step.type === 'tiles') {
        this.stepContainer.innerHTML = `
          <h2 class="cf-step__title">${this.currentStep + 1}. ${escapeHtml(step.question)}</h2>
          <div class="cf-options cf-options--tiles" role="radiogroup" aria-label="${escapeHtml(step.question)}">
            ${step.options
              .map(
                (opt) => `
              <button type="button" class="cf-option${selected === opt.value ? ' is-selected' : ''}"
                data-value="${escapeHtml(opt.value)}" role="radio" aria-checked="${selected === opt.value}">
                <span class="cf-option__icon">${opt.icon || ''}</span>
                <span class="cf-option__label">${escapeHtml(opt.label)}</span>
              </button>`
              )
              .join('')}
          </div>`;

        this.stepContainer.querySelectorAll('.cf-option').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.answers[step.id] = btn.dataset.value;
            this.stepContainer.querySelectorAll('.cf-option').forEach((b) => {
              b.classList.toggle('is-selected', b === btn);
              b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
            });
            this.updateNav();
          });
        });
      } else if (step.type === 'list') {
        this.stepContainer.innerHTML = `
          <h2 class="cf-step__title">${this.currentStep + 1}. ${escapeHtml(step.question)}</h2>
          <div class="cf-options cf-options--list" role="radiogroup" aria-label="${escapeHtml(step.question)}">
            ${step.options
              .map(
                (opt) => `
              <label class="cf-radio${selected === opt.value ? ' is-selected' : ''}">
                <input type="radio" name="cf-${escapeHtml(step.id)}" value="${escapeHtml(opt.value)}"${selected === opt.value ? ' checked' : ''}>
                <span class="cf-radio__mark"></span>
                <span class="cf-radio__label">${escapeHtml(opt.label)}</span>
              </label>`
              )
              .join('')}
          </div>`;

        this.stepContainer.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', () => {
            this.answers[step.id] = input.value;
            this.stepContainer.querySelectorAll('.cf-radio').forEach((label) => {
              label.classList.toggle('is-selected', label.querySelector('input') === input);
            });
            this.updateNav();
          });
        });
      }

      this.renderProgress();
      this.stepContainer.focus({ preventScroll: true });
    }

    updateNav() {
      const step = this.steps[this.currentStep];
      const hasAnswer = step && this.answers[step.id] != null;

      if (this.backBtn) {
        this.backBtn.hidden = this.currentStep === 0;
      }

      if (this.nextBtn) {
        const isLast = this.currentStep === this.steps.length - 1;
        this.nextBtn.textContent = isLast
          ? this.labels.seeResults || 'See Results'
          : this.labels.next || 'Next';
        this.nextBtn.disabled = !hasAnswer;
      }
    }

    goBack() {
      if (this.currentStep === 0) return;
      this.currentStep -= 1;
      this.renderStep();
      this.updateNav();
    }

    goNext() {
      const step = this.steps[this.currentStep];
      if (!step || this.answers[step.id] == null || this.nextBtn?.disabled) return;

      if (this.currentStep < this.steps.length - 1) {
        this.currentStep += 1;
        this.renderStep();
        this.updateNav();
        return;
      }

      this.showResults();
    }

    showResults() {
      const { recommended, alternatives } = matchProducts(this.products, this.answers);

      if (this.wizardEl) this.wizardEl.hidden = true;
      if (this.resultsEl) {
        this.resultsEl.hidden = false;
        this.resultsEl.innerHTML = this.buildResultsHtml(recommended, alternatives);
        this.bindResultsActions();
      }

      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    buildResultsHtml(recommended, alternatives) {
      if (!recommended) {
        return `<div class="cf-empty"><p>${escapeHtml(this.labels.noResults || 'No matching cameras found.')}</p></div>`;
      }

      const features = (recommended.features || [])
        .map(
          (f) =>
            `<li><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="#22a06b"/><path d="M4.5 8.2l2.2 2.2 4.8-5" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>${escapeHtml(f)}</li>`
        )
        .join('');

      const altHtml = (alternatives || [])
        .filter(Boolean)
        .map(
          (p) => `
        <article class="cf-alt-card">
          <img src="${escapeHtml(productImage(p))}" alt="${escapeHtml(p.title)}" width="80" height="80" loading="lazy">
          <div class="cf-alt-card__info">
            <h4>${escapeHtml(p.title)}</h4>
            <p class="cf-alt-card__price">${formatMoney(p.price, this.moneyFormat)}</p>
          </div>
          <a href="${escapeHtml(p.url)}" class="cf-btn cf-btn--outline cf-btn--sm">${escapeHtml(this.labels.view || 'View')}</a>
        </article>`
        )
        .join('');

      const compareHtml =
        recommended.compareAtPrice > recommended.price
          ? `<s class="cf-hero__compare">${formatMoney(recommended.compareAtPrice, this.moneyFormat)}</s> `
          : '';

      return `
        <div class="cf-results__grid">
          <article class="cf-hero">
            <span class="cf-badge">${escapeHtml(this.labels.bestMatch || 'Best Match')}</span>
            <div class="cf-hero__image">
              <img src="${escapeHtml(productImage(recommended))}" alt="${escapeHtml(recommended.title)}" loading="lazy">
            </div>
            <div class="cf-hero__body">
              <h2>${escapeHtml(recommended.title)}</h2>
              ${recommended.subtitle ? `<p class="cf-hero__subtitle">${escapeHtml(recommended.subtitle)}</p>` : ''}
              <ul class="cf-hero__features">${features}</ul>
              <p class="cf-hero__price">${compareHtml}${formatMoney(recommended.price, this.moneyFormat)}</p>
              <div class="cf-hero__actions">
                <a href="${escapeHtml(recommended.url)}" class="cf-btn cf-btn--primary">${escapeHtml(this.labels.viewProduct || 'View Product')}</a>
                <button type="button" class="cf-btn cf-btn--outline" data-add-to-cart="${recommended.variantId}"${recommended.available ? '' : ' disabled'}>
                  ${escapeHtml(recommended.available ? this.labels.addToCart || 'Add to Cart' : this.labels.soldOut || 'Sold Out')}
                </button>
              </div>
            </div>
          </article>
          <aside class="cf-alts">
            <h3>${escapeHtml(this.labels.otherOptions || 'Other Great Options')}</h3>
            <div class="cf-alts__list">${altHtml || `<p class="cf-alts__empty">${escapeHtml(this.labels.noAlternatives || '')}</p>`}</div>
            <button type="button" class="cf-btn cf-btn--ghost" data-restart>${escapeHtml(this.labels.startOver || 'Start Over')}</button>
          </aside>
        </div>`;
    }

    bindResultsActions() {
      this.resultsEl.querySelector('[data-restart]')?.addEventListener('click', () => {
        this.currentStep = 0;
        this.answers = {};
        if (this.resultsEl) {
          this.resultsEl.hidden = true;
          this.resultsEl.innerHTML = '';
        }
        if (this.wizardEl) this.wizardEl.hidden = false;
        this.renderStep();
        this.updateNav();
      });

      this.resultsEl.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const variantId = btn.dataset.addToCart;
          if (!variantId || btn.disabled) return;

          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');

          try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
            });

            if (response.ok) {
              const cartContent = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
              document.documentElement.dispatchEvent(
                new CustomEvent('cart:change', {
                  bubbles: true,
                  detail: { baseEvent: 'variant:add', cart: cartContent },
                })
              );
              btn.textContent = this.labels.added || 'Added!';
            } else {
              btn.textContent = this.labels.error || 'Error';
            }
          } catch {
            btn.textContent = this.labels.error || 'Error';
          } finally {
            btn.removeAttribute('aria-busy');
            setTimeout(() => {
              btn.disabled = false;
              btn.textContent = this.labels.addToCart || 'Add to Cart';
            }, 2000);
          }
        });
      });
    }
  }

  window.customElements.define('camera-finder', CameraFinder);
})();
